// Copyright ©️ 2026 eVolve MEP, LLC

import {
  type WorkCellTask,
  type TaskStatusTypeId,
  formatDate,
  type CustomProperty,
  type WorkCellId,
  type RenderableUser,
} from '@evolvemep/api-ts';
import { Flex } from '@mantine/core';
import type { ColDef, ValueFormatterParams } from 'ag-grid-enterprise';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AttachmentsPopover } from 'components/Mantine/Attachments/AttachmentsPopover';
import { CellRenderer } from 'components/Mantine/CellRenderer';
import { hoursColDef } from 'components/Mantine/commonColumnDefs';
import { getCustomPropertyColDefs } from 'components/Mantine/CustomProperties/getCustomPropertyColDefs';
import { EntityStatusBadge } from 'components/Mantine/EntityStatusBadge';
import { MultiUserSelect } from 'components/Mantine/MultiUserSelect';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { UserInfoList } from 'components/Mantine/UserInfoBadge';
import { lockedColDef } from 'helpers/ag-grid/baseColumnDef';
import { editableColDef } from 'helpers/ag-grid/EditableCells/editableColDef';
import { EntitySetFilterComponent, type EntityStateAgGridContext } from 'helpers/ag-grid/EntitySetFilterComponent';
import { getScheduleColDefs } from 'helpers/ag-grid/getScheduleColDefs';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { formatQuantity } from 'helpers/formatQuantity';
import { isNotNil } from 'helpers/isNotNil';
import { validateUserNameOrEmail } from 'helpers/userHelpers';
import { ActionTaskMenu } from 'modules/Shop/Tasks/ActionTaskMenu';
import { TaskPlayPauseIcon } from 'modules/Shop/Tasks/TaskPlayPauseIcon';
import { WorkCellTaskLink } from 'modules/Shop/Tasks/WorkCellTaskLink';
import { WorkCellSelect } from 'modules/Shop/WorkOrders/WorkOrderDetail/Tasks/MoveWorkCell/WorkCellSelect';

export const getWorkCellsColDef = (
  isActive: boolean,
  customTaskProperties: CustomProperty[],
): ColDef<WorkCellTask>[] => [
  {
    field: 'taskName',
    lockVisible: true,
    pinned: true,
    flex: 1,
    minWidth: 220,
    cellRenderer: CellRenderer<WorkCellTask>(({ data }) => <WorkCellTaskLink task={data} from="Tasks" />),
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'workOrderItemName',
    headerName: 'Item Name',
    minWidth: 220,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'quantity',
    headerName: 'Item Qty',
    valueFormatter: ({ value }: { value: number | undefined }) => formatQuantity(value),
    sortable: false,
    width: 100,
  },
  editableColDef<
    WorkCellTask,
    'workCellId',
    {
      workCellId: WorkCellId;
      workCellName: string;
    },
    typeof WorkCellSelect
  >({
    field: 'workCellId',
    headerName: 'Work Cell',
    valueGetter: ({ data }) =>
      isNotNil(data)
        ? {
            workCellId: data.workCellId,
            workCellName: data.workCellName,
          }
        : null,
    valueFormatter: ({ data }) => data?.workCellName ?? '--',
    dataMapper: (_, updates) => ({ workCellId: updates?.workCellId }),
    Editor: WorkCellSelect,
    editorProps: (data) => ({
      taskIds: [data.taskId],
      facility: {
        facilityId: data.facilityId,
        facilityName: data.facilityName,
      },
    }),
    editable: (t) => t.taskStatusTypeName !== 'Completed',
    closeOnChange: true,
  }),
  {
    field: 'workOrderIdentifier',
    headerName: 'Work Order Id',
    cellRenderer: CellRenderer<WorkCellTask>(({ value, data }) => (
      <EvolveLink to={`/work-orders/${data.workOrderId}`} from="Tasks">
        <Flex align="center" gap="xs">
          <EvolveIcon icon="WorkOrder" light />
          {value}
        </Flex>
      </EvolveLink>
    )),
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'projectName',
    headerName: 'Project',
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  {
    field: 'taskStatusTypeId',
    headerName: 'Status',
    minWidth: 150,
    width: 120,
    valueFormatter: ({
      value,
      context: { entityStates },
    }: {
      value: TaskStatusTypeId | null;
      context: EntityStateAgGridContext<'Task'>;
    }) => entityStates.find((t) => t.stateId === value)?.name ?? '--',
    cellRenderer: CellRenderer<WorkCellTask>(({ data }) => (
      <Flex gap={6} align="center">
        <EntityStatusBadge customStatusEntityName="Task" stateName={data.taskStatusTypeName} />
        <TaskPlayPauseIcon taskStatusTypeName={data.taskStatusTypeName} />
      </Flex>
    )),
    sortable: false,
    filter: EntitySetFilterComponent<WorkCellTask, 'Task'>('taskStatusTypeIds'),
  },
  {
    field: 'workOrderName',
    headerName: 'Work Order Name',
    minWidth: 220,
    filter: 'agTextColumnFilter',
    filterParams: {
      filterOptions: ['contains'],
      maxNumConditions: 1,
      buttons: ['reset'],
    },
  },
  editableColDef({
    field: 'assignees',
    // Used for grid export
    valueFormatter: ({ data }) => data?.assignees.map(({ user }) => validateUserNameOrEmail(user)).join(', ') ?? '--',
    cellRenderer: CellRenderer<WorkCellTask>(({ data }) => (
      <UserInfoList users={data.assignees.map(({ user }) => user)} />
    )),
    valueGetter: ({ data }): RenderableUser[] | null => data?.assignees.map(({ user }) => user) ?? null,
    dataMapper: (_, users) => ({
      assignees: users?.map((user) => ({ user, totalSeconds: 0, isActive: false })) ?? [],
    }),
    width: 270,
    sortable: false,
    Editor: MultiUserSelect,
    editable: true,
  }),
  {
    field: 'comments',
    headerName: 'Task Comments',
    sortable: false,
  },
  {
    field: 'adjustedTimeInSeconds',
    headerName: 'Adjusted Time',
    ...hoursColDef(),
    width: 125,
    sortable: false,
  },
  {
    field: 'totalTimeEntriesInSeconds',
    headerName: 'Logged Time',
    ...hoursColDef(),
    width: 125,
    sortable: false,
  },
  {
    field: 'estimatedTimeInSeconds',
    headerName: 'Estimated Time',
    headerTooltip: 'The Estimated Unit time of this task multiplied by the item quantity.',
    ...hoursColDef(),
    width: 160,
    sortable: false,
  },
  {
    field: 'currentRunTimeInSeconds',
    headerName: 'Run Time',
    ...hoursColDef(),
    width: 125,
    sortable: false,
  },
  ...getScheduleColDefs<WorkCellTask>({
    hideDaysEarlyLate: isActive,
    getIsCompleted: (t) => t.taskStatusTypeName === 'Completed',
  }),
  {
    field: 'dateStarted',
    width: 150,
    valueFormatter: ({ value }: ValueFormatterParams<WorkCellTask, WorkCellTask['dateStarted']>) =>
      formatDate(value, 'MM/dd/yyyy'),
    sortable: false,
  },
  {
    field: 'dateCompleted',
    width: 150,
    valueFormatter: ({ value }: ValueFormatterParams<WorkCellTask, WorkCellTask['dateCompleted']>) =>
      formatDate(value, 'MM/dd/yyyy'),
    sortable: false,
  },
  ...getCustomPropertyColDefs<WorkCellTask>(customTaskProperties),
  {
    ...lockedColDef('right', false),
    width: 100,
    cellRenderer: CellRenderer<WorkCellTask>(({ data }) => (
      <AttachmentsPopover taskId={data.taskId} numberOfAttachments={data.numberOfAttachments} allowUploads="task" />
    )),
  },
  {
    ...lockedColDef('right'),
    width: 50,
    cellRenderer: CellRenderer<WorkCellTask>(({ data, api, node }) => (
      <ActionTaskMenu
        task={data}
        workCellTask={data}
        onTaskUpdated={(updatedTask) => {
          if (isActive && updatedTask.taskStatusTypeName === 'Completed') {
            refreshGrid(api, node);
          }
          node.updateData({
            ...data,
            ...updatedTask,
          });
          api.refreshCells({ rowNodes: [node] });
        }}
        parentPage="Tasks"
        facility={{
          facilityId: data.facilityId,
          facilityName: data.facilityName,
        }}
      />
    )),
  },
];
